import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Globe, X } from 'lucide-react'
import { languages } from '../../i18n'

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // 检测移动端
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // 阻止移动端模态框时的背景滚动
  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMobile, isOpen])

  // 切换语言
  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode)
    setIsOpen(false)
    
    // 处理RTL语言的文本方向
    const rtlLanguages = ['ar']
    const isRTL = rtlLanguages.includes(languageCode)
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr'
    document.documentElement.lang = languageCode
  }

  // 初始化时设置RTL
  useEffect(() => {
    const rtlLanguages = ['ar']
    const isRTL = rtlLanguages.includes(i18n.language)
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr'
    document.documentElement.lang = i18n.language
  }, [i18n.language])

  // 获取当前语言信息
  const currentLanguage = languages[i18n.language as keyof typeof languages] || languages.en

  return (
    <div className="relative" ref={dropdownRef}>
      {/* 语言切换按钮 - 移动端和桌面端响应式设计 */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-2 px-3 py-2 bg-white border-2 border-primary-200 text-primary-700 hover:text-primary-900 hover:border-primary-400 hover:bg-primary-50 transition-all duration-200 rounded-lg shadow-md hover:shadow-lg font-medium ${
          isMobile ? 'text-sm w-full justify-center' : 'text-base'
        }`}
        aria-label="Language switcher"
      >
        <Globe className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} text-primary-600`} />
        <span className="font-semibold">
          {isMobile ? currentLanguage.slice(0, 4) : currentLanguage}
        </span>
        <ChevronDown 
          className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} transition-transform duration-200 text-primary-600 ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {/* 移动端全屏模态框 */}
      {isMobile && isOpen && (
        <>
          {/* 遮罩层 */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setIsOpen(false)}
          />
          
          {/* 模态框内容 */}
          <div className="fixed inset-x-0 bottom-0 bg-white rounded-t-3xl z-50 max-h-[80vh] overflow-hidden language-modal">
            {/* 头部 */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">选择语言 / Select Language</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="p-3 rounded-full hover:bg-gray-100 transition-colors mobile-touch-target"
                aria-label="关闭语言选择"
              >
                <X className="w-6 h-6 text-gray-500" />
              </button>
            </div>
            
            {/* 语言列表 */}
            <div className="overflow-y-auto max-h-[calc(80vh-80px)]">
              {Object.entries(languages).map(([code, language]) => (
                <button
                  key={code}
                  onClick={() => handleLanguageChange(code)}
                  className={`w-full text-left p-6 border-b border-gray-100 hover:bg-blue-50 transition-all duration-200 flex items-center justify-between mobile-language-button mobile-touch-target ${
                    i18n.language === code 
                      ? 'bg-blue-100 border-l-4 border-l-blue-500' 
                      : ''
                  }`}
                >
                  <div className="flex flex-col">
                    <span className={`text-lg font-medium ${
                      i18n.language === code ? 'text-blue-800' : 'text-gray-900'
                    }`}>
                      {language}
                    </span>
                    <span className="text-sm text-gray-500">
                      {code.toUpperCase()}
                    </span>
                  </div>
                  {i18n.language === code && (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                      <span className="text-lg font-bold text-blue-600">✓</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* 桌面端下拉菜单 */}
      {!isMobile && isOpen && (
        <div className="absolute right-0 mt-3 w-56 bg-white rounded-xl shadow-2xl border-2 border-primary-100 py-2 z-50 backdrop-blur-sm">
          <div className="px-3 py-2 border-b border-primary-100">
            <p className="text-xs font-medium text-primary-600 uppercase tracking-wide">选择语言 / Select Language</p>
          </div>
          {Object.entries(languages).map(([code, language]) => (
            <button
              key={code}
              onClick={() => handleLanguageChange(code)}
              className={`w-full text-left px-4 py-3 text-base hover:bg-primary-50 transition-all duration-150 flex items-center justify-between group ${
                i18n.language === code 
                  ? 'bg-primary-100 text-primary-800 font-semibold border-r-4 border-primary-500' 
                  : 'text-gray-700 hover:text-primary-700'
              }`}
            >
              <div className="flex flex-col">
                <span className={`font-medium ${i18n.language === code ? 'text-primary-800' : 'text-gray-800'}`}>
                  {language}
                </span>
                <span className="text-sm text-gray-500 group-hover:text-primary-500">
                  {code.toUpperCase()}
                </span>
              </div>
              {i18n.language === code && (
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-primary-500 rounded-full animate-pulse"></div>
                  <span className="text-xs font-bold text-primary-600">✓</span>
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default LanguageSwitcher 